import {
  displayTitle,
  processedTokens,
  type QuotaView,
  type TokenTally,
  type WorkOrderUsage,
} from "@szyos/domain";
import { Link } from "@tanstack/react-router";
import { QUOTA_LABEL, tokens, untilText } from "../format";
import { Icon } from "../icons";
import { useSimulation } from "../simulation";

/** Neu verarbeitete Tokens mit Erklärung der Zählweise. */
export function TokenFigure({ tally, label }: { tally: TokenTally; label: string }) {
  return (
    <div className="token-figure">
      <strong className="token-figure-value">{tokens(processedTokens(tally))}</strong>
      <span className="token-figure-label">{label}</span>
      <small className="muted">
        {tally.runs} {tally.runs === 1 ? "Lauf" : "Läufe"} · Ausgabe {tokens(tally.outputTokens)}
        {tally.cacheReadTokens > 0 ? ` · aus Cache ${tokens(tally.cacheReadTokens)}` : ""}
      </small>
    </div>
  );
}

const LIMIT_STATE: Record<QuotaView["state"], string> = {
  ok: "im Rahmen",
  warning: "Warnschwelle erreicht",
  exhausted: "Limit erreicht",
  reset: "inzwischen zurückgesetzt",
};

/** Limitstand, wie Claude Code ihn zuletzt gemeldet hat. Fehlt ein Anteil, wird keiner geschätzt. */
export function LimitList({ limits }: { limits: QuotaView[] }) {
  if (limits.length === 0) {
    return (
      <p className="muted">
        Noch kein Limitstand gemeldet. Claude meldet ihn mit dem nächsten Lauf über dein Abo.
      </p>
    );
  }
  return (
    <ul className="limit-list">
      {limits.map((l) => {
        const share = l.remainingShare;
        return (
          <li key={l.limit} className={`limit-row is-${l.state}`}>
            <span className="limit-head">
              <strong>{QUOTA_LABEL[l.limit]}</strong>
              <span className="chip limit-chip">{LIMIT_STATE[l.state]}</span>
            </span>
            {share !== null && l.state !== "reset" && (
              <span className="limit-bar">
                <span className="bar" role="img" aria-label={`Noch ${Math.round(share * 100)} Prozent frei`}>
                  <i
                    style={{
                      ["--w" as string]: `${Math.round(share * 100)}%`,
                      ["--accent" as string]: share < 0.2 ? "var(--red)" : "var(--lime)",
                    }}
                  />
                </span>
                <span className="mono">noch {Math.round(share * 100)} % frei</span>
              </span>
            )}
            <small className="muted">
              {share === null && l.state === "ok" ? "Claude nennt dazu keinen Prozentwert. " : ""}
              {l.resetsAt && l.state !== "reset" ? `Rücksetzung ${untilText(l.resetsAt)}. ` : ""}
              Gemeldet {untilText(l.observedAt)}.
            </small>
          </li>
        );
      })}
    </ul>
  );
}

/** Tokens eines Auftrags je Aufgabe, für die Auftragsseite. */
export function WorkOrderTokens({ usage }: { usage: WorkOrderUsage | undefined }) {
  const simulation = useSimulation();
  return (
    <section className="panel token-panel" aria-labelledby="woTokensTitle">
      <div className="panel-head">
        <h2 id="woTokensTitle">Token-Verbrauch</h2>
        <Link to="/usage" className="link">
          Übersicht <Icon name="arrow" />
        </Link>
      </div>
      {!usage ? (
        <p className="muted">Lade Verbrauch …</p>
      ) : usage.total.runs === 0 ? (
        <p className="muted">
          Noch kein Lauf über einen echten Anbieter.{simulation ? " Mock-Läufe zählen nicht." : ""}
        </p>
      ) : (
        <>
          <div className="token-figures">
            <TokenFigure tally={usage.total} label="Auftrag gesamt" />
            <TokenFigure tally={usage.week} label="davon diese Woche" />
          </div>
          <table className="token-table">
            <caption className="sr-only">Tokens je Aufgabe</caption>
            <thead>
              <tr>
                <th scope="col">Aufgabe</th>
                <th scope="col">Läufe</th>
                <th scope="col">Tokens</th>
              </tr>
            </thead>
            <tbody>
              {usage.planning.runs > 0 && (
                <tr>
                  <td>Planvorschlag (Planungsstudio)</td>
                  <td className="mono">{usage.planning.runs}</td>
                  <td className="mono">{tokens(processedTokens(usage.planning))}</td>
                </tr>
              )}
              {usage.items.map((i) => (
                <tr key={i.workItemId}>
                  <td>{displayTitle(i.title)}</td>
                  <td className="mono">{i.total.runs}</td>
                  <td className="mono">{tokens(processedTokens(i.total))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}
